import { motion } from 'framer-motion';
import { Share, Plus, Heart, Camera } from 'lucide-react';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import instagramLogo from '@/assets/instagram-logo.png';
import tiktokLogo from '@/assets/tiktok-logo.png';
import amazonLogo from '@/assets/amazon-logo.svg';
import pinterestLogo from '@/assets/pinterest-logo.png';
import nikeLogo from '@/assets/nike-logo.svg';
import zaraLogo from '@/assets/zara-logo.svg';
import sheinLogo from '@/assets/shein-logo.svg';
import wantIcon from '@/assets/want-w-icon.svg';

interface HowToSaveSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SOURCE_PILLS = [
  { name: 'Instagram', logo: instagramLogo },
  { name: 'TikTok', logo: tiktokLogo },
  { name: 'Amazon', logo: amazonLogo },
  { name: 'Pinterest', logo: pinterestLogo },
  { name: 'Nike', logo: nikeLogo },
  { name: 'Zara', logo: zaraLogo },
  { name: 'SHEIN', logo: sheinLogo },
  { name: 'Browser', logo: null },
  { name: 'Screenshots', logo: null },
];

export function HowToSaveSheet({ open, onOpenChange }: HowToSaveSheetProps) {
  return (
    <DraggableBottomSheet
      open={open}
      onOpenChange={onOpenChange}
      showCloseButton={true}
      height="auto"
    >
      <div className="px-5 pb-8 space-y-6">
        {/* Section 1: Share from anywhere */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-foreground text-center">
            Share from anywhere
          </h3>

          {/* Scrolling source pills → share arrow → WNTD icon */}
          <div className="flex items-center justify-center gap-4">
            {/* Left: auto-scrolling pill marquee */}
            <div className="flex-1 overflow-hidden rounded-2xl">
              <div 
                className="flex flex-col gap-2"
              >
                {/* Row 1 - scrolls left */}
                <div className="overflow-hidden">
                  <div 
                    className="flex items-center gap-2 w-max"
                    style={{ animation: 'marquee 18s linear infinite' }}
                  >
                    {[...SOURCE_PILLS.slice(0, 5), ...SOURCE_PILLS.slice(0, 5)].map((source, idx) => (
                      <span
                        key={`r1-${idx}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap bg-muted/60 text-muted-foreground border border-border/40"
                      >
                        {source.logo && (
                          <img src={source.logo} alt={source.name} className="w-4 h-4 object-contain" />
                        )}
                        {source.name}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Row 2 - scrolls right */}
                <div className="overflow-hidden">
                  <div 
                    className="flex items-center gap-2 w-max"
                    style={{ animation: 'marquee-reverse 22s linear infinite' }}
                  >
                    {[...SOURCE_PILLS.slice(5), ...SOURCE_PILLS.slice(0, 4), ...SOURCE_PILLS.slice(5), ...SOURCE_PILLS.slice(0, 4)].map((source, idx) => (
                      <span
                        key={`r2-${idx}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap bg-muted/60 text-muted-foreground border border-border/40"
                      >
                        {source.logo && (
                          <img src={source.logo} alt={source.name} className="w-4 h-4 object-contain" />
                        )}
                        {source.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Center: share icon + flowing dots */}
            <div className="flex flex-col items-center gap-1 shrink-0">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-1 h-1 rounded-full bg-primary/50"
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.25,
                  }}
                />
              ))}
              <motion.div
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Share className="w-5 h-5 text-primary" />
              </motion.div>
            </div>

            {/* Right: large WNTD destination icon */}
            <motion.div
              className="w-16 h-16 rounded-2xl bg-background border border-border/50 flex items-center justify-center shadow-md shrink-0"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: 1,
                scale: 1,
                boxShadow: [
                  '0 4px 16px hsl(var(--primary) / 0)',
                  '0 4px 24px hsl(var(--primary) / 0.3)',
                  '0 4px 16px hsl(var(--primary) / 0)',
                ],
              }}
              transition={{
                opacity: { delay: 0.4, duration: 0.4 },
                scale: { delay: 0.4, duration: 0.4 },
                boxShadow: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
              }}
            >
              <img src={wantIcon} alt="WNTD" className="w-9 h-9" />
            </motion.div>
          </div>

          {/* Copy */}
          <p className="text-sm text-muted-foreground text-center leading-relaxed max-w-xs mx-auto">
            See something you love in any app? Tap the{' '}
            <span className="font-semibold text-foreground">Share</span> button
            and send it straight to WNTD.
          </p>

          {/* Mentions */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {[
              { icon: Camera, label: 'Camera roll' },
            ].map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-full px-3 py-1"
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-full px-3 py-1">
              Shopping apps
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-full px-3 py-1">
              Any browser
            </span>
          </div>

          <p className="text-[11px] text-muted-foreground/70 text-center">
            Works everywhere the share button appears
          </p>
        </div>

        {/* Separator with "or" pill */}
        <div className="relative flex items-center py-1">
          <div className="flex-1 border-t border-border" />
          <span className="mx-3 text-xs font-medium text-muted-foreground bg-background px-3 py-0.5 rounded-full border border-border">
            or
          </span>
          <div className="flex-1 border-t border-border" />
        </div>

        {/* Section 2: Find and save inside WNTD */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-foreground text-center">
            Find and save inside WNTD
          </h3>

          <div className="grid grid-cols-2 gap-3">
            {/* Plus card */}
            <div className="flex flex-col items-center text-center p-5 rounded-2xl bg-muted/40 border border-border/50 space-y-2.5">
              <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Plus className="w-8 h-8 text-primary" />
              </div>
              <p className="text-sm font-semibold text-foreground">Paste, snap, scan</p>
              <p className="text-[11px] text-muted-foreground leading-snug">
                Links, photos & barcodes
              </p>
            </div>

            {/* Heart card */}
            <div className="flex flex-col items-center text-center p-5 rounded-2xl bg-muted/40 border border-border/50 space-y-2.5">
              <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <p className="text-sm font-semibold text-foreground">Tap to save</p>
              <p className="text-[11px] text-muted-foreground leading-snug">
                On any product in WNTD
              </p>
            </div>
          </div>
        </div>
      </div>
    </DraggableBottomSheet>
  );
}
